import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n";
import { trackReserveClick } from "@/lib/analytics";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t, lang, localePath } = useI18n();
  const homePath = lang === "en" ? "/en" : "/";
  const isHome = location.pathname === homePath || location.pathname === "/en/";
  const overHero = isHome && !scrolled;

  const NAV = [
    { to: localePath("/"), key: "home", label: t("nav.home"), end: true },
    { to: localePath("/menu"), key: "menu", label: t("nav.menu") },
    { to: localePath("/evenements"), key: "events", label: t("nav.events") },
    { to: localePath("/galerie"), key: "gallery", label: t("nav.gallery") },
    { to: localePath("/carte-cadeau"), key: "giftcard", label: t("nav.giftcard") },
    { to: localePath("/contact"), key: "contact", label: t("nav.contact") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const textBase = overHero ? "text-white/85" : "text-[#203240]/80";
  const textActive = overHero ? "text-white" : "text-[#B8956A]";
  const LOGO_URL = "/logo.webp";

  return (
    <header
      data-testid="public-header"
      className={`fixed top-[52px] left-0 right-0 z-[80] transition-all duration-500 ${overHero ? "header-on-hero" : "glass-header"}`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between h-24">
        <Link to={homePath} data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src={LOGO_URL}
            alt="L'Entracte — Restaurant & Réception · Ville d'Avray"
            className={`h-16 md:h-20 w-auto object-contain transition-all duration-500 ${overHero ? "brightness-0 invert" : ""}`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.key}
              to={n.to}
              data-testid={`nav-link-${n.key}`}
              end={n.end}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.24em] font-medium transition-colors ${isActive ? textActive : `${textBase} hover:text-[#B8956A]`}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <LanguageSwitcher variant={overHero ? "dark" : "light"} />
          <Link
            to={localePath("/reservation")}
            data-testid="nav-cta-reservation"
            onClick={() => trackReserveClick("navbar_desktop")}
            className={overHero ? "btn-ghost-light" : "btn-ghost"}
          >
            {t("nav.reserve")}
          </Link>
        </div>

        <button
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((s) => !s)}
          className={`lg:hidden p-2 ${overHero ? "text-white" : "text-[#203240]"}`}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden glass-header border-t border-[#D9D3C5]">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.key}
                to={n.to}
                end={n.end}
                data-testid={`nav-mobile-${n.key}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.22em] ${isActive ? "text-[#B8956A]" : "text-[#203240]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="pt-2">
              <LanguageSwitcher variant="light" />
            </div>
            <Link to={localePath("/reservation")} data-testid="nav-mobile-reservation" onClick={() => trackReserveClick("navbar_mobile")} className="btn-ghost mt-2 w-full justify-center">
              {t("nav.reserve")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
